import { apiUrl } from "../utils/api";

/**
 * Antwort des Backends auf „wie öffne ich dieses Dokument im Office-Editor?".
 *
 * `config` wird UNVERÄNDERT an ONLYOFFICE weitergereicht. Sie ist serverseitig
 * signiert (`config.token`) — wer daran etwas ändert, dessen Konfiguration
 * lehnt der Document Server ab. Deshalb ist sie hier bewusst als undurchsichtige
 * Struktur typisiert und nicht als ausmodelliertes Objekt: es gibt nichts darin,
 * das das Frontend anfassen dürfte.
 */
export interface OfficeEditorConfig {
    /** Basis-URL des Document Servers aus Browsersicht, z. B. `/onlyoffice`. */
    documentServerUrl: string;
    /** `false` = Leseansicht (fehlendes Schreibrecht oder ein Altformat). */
    editable: boolean;
    config: Record<string, unknown>;
}

export interface OfficeProviderStatus {
    enabled: boolean;
    provider: 'spe' | 'onlyoffice' | 'disabled';
    hasActiveSpeWorkspaces: boolean;
}
let statusPromise: Promise<OfficeProviderStatus> | null = null;
let cachedAt = 0;
const unavailable: OfficeProviderStatus = { enabled: false, provider: 'disabled', hasActiveSpeWorkspaces: false };
export const officeEditingService = {
    async status({ fresh = false }: { fresh?: boolean } = {}): Promise<OfficeProviderStatus> {
        if (fresh || !statusPromise || Date.now() - cachedAt >= 30_000) {
            cachedAt = Date.now();
            statusPromise = fetch(apiUrl("documents/office/status"))
              .then(res => res.ok ? res.json() : unavailable)
              .then(data => ({
                enabled: data.enabled === true && (data.provider === 'spe' || data.provider === 'onlyoffice'),
                provider: data.provider === 'spe' || data.provider === 'onlyoffice' ? data.provider : 'disabled',
                hasActiveSpeWorkspaces: data.hasActiveSpeWorkspaces === true,
              } as OfficeProviderStatus))
              .catch(() => unavailable);
        }
        return statusPromise;
    },
    async isEnabled(): Promise<boolean> {
        const status = await this.status();
        return status.enabled || status.hasActiveSpeWorkspaces;
    },
    resetStatusCache(): void { statusPromise = null; cachedAt = 0; },

    async config(documentId: number): Promise<OfficeEditorConfig> {
        const res = await fetch(apiUrl(`documents/${documentId}/office/config`));
        if (!res.ok) {
            throw new Error(`Editor konnte nicht geöffnet werden: ${res.status}`);
        }
        return res.json();
    },
};
